import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSend, FiCheckCircle } from 'react-icons/fi';
import { submitApplication, getCourses } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const ApplicationForm = ({ preselectedCourse }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    collegeName: user?.collegeName || '',
    yearOfStudy: user?.yearOfStudy || '',
    course: preselectedCourse || '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getCourses().then(res => setCourses(res.data.courses || []));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Phone number required';
    if (!form.collegeName.trim()) e.collegeName = 'College name required';
    if (!form.yearOfStudy) e.yearOfStudy = 'Year of study required';
    if (!form.course) e.course = 'Please select a course';
    return e;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const res = await submitApplication(form);
      setSuccess(res.data.application);
      toast.success('Application submitted! Check your email for confirmation.');
    } catch (err) {
      const msg = err.response?.data?.message || 'Submission failed. Please try again.';
      toast.error(msg);
      if (err.response?.data?.errors) {
        const apiErrors = {};
        err.response.data.errors.forEach(e => { apiErrors[e.path] = e.msg; });
        setErrors(apiErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="apply-success">
      <style>{`
        .apply-success { text-align: center; padding: 48px 32px; }
        .apply-success-icon { width: 80px; height: 80px; background: #d1fae5; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; }
        .apply-success h2 { font-size: 26px; margin-bottom: 10px; }
        .apply-success .app-num { display: inline-block; background: var(--primary-light); color: var(--primary); padding: 8px 20px; border-radius: var(--radius); font-size: 18px; font-weight: 700; font-family: var(--font-display); margin: 16px 0; }
        .apply-success-actions { display: flex; gap: 12px; justify-content: center; margin-top: 24px; flex-wrap: wrap; }
      `}</style>
      <div className="apply-success-icon"><FiCheckCircle size={40} color="var(--success)" /></div>
      <h2>Application Submitted! 🎉</h2>
      <p className="text-mid">A confirmation has been sent to <strong>{form.email}</strong></p>
      <div className="app-num">{success.applicationNumber}</div>
      <p className="text-mid" style={{ fontSize: 14 }}>Save this application number for reference. Our team will review and respond within 24–48 hours.</p>
      <div className="apply-success-actions">
        {user ? (
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard</button>
        ) : (
          <>
            <button className="btn btn-primary" onClick={() => navigate('/register')}>Create Account</button>
            <button className="btn btn-outline" onClick={() => navigate('/')}>Back to Home</button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <style>{`
        .form-section-title { font-size: 13px; font-weight: 600; color: var(--mid); text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid var(--border); }
      `}</style>

      <div className="form-section-title">Personal Information</div>
      <div className="form-grid">
        <div className="form-group">
          <label>Full Name <span>*</span></label>
          <input name="fullName" value={form.fullName} onChange={handleChange} className={`form-control${errors.fullName ? ' error' : ''}`} placeholder="Rahul Sharma" />
          {errors.fullName && <div className="form-error">{errors.fullName}</div>}
        </div>
        <div className="form-group">
          <label>Email Address <span>*</span></label>
          <input name="email" type="email" value={form.email} onChange={handleChange} className={`form-control${errors.email ? ' error' : ''}`} placeholder="rahul@example.com" />
          {errors.email && <div className="form-error">{errors.email}</div>}
        </div>
        <div className="form-group">
          <label>Phone Number <span>*</span></label>
          <input name="phone" value={form.phone} onChange={handleChange} className={`form-control${errors.phone ? ' error' : ''}`} placeholder="+91 98765 43210" />
          {errors.phone && <div className="form-error">{errors.phone}</div>}
        </div>
        <div className="form-group">
          <label>College / University <span>*</span></label>
          <input name="collegeName" value={form.collegeName} onChange={handleChange} className={`form-control${errors.collegeName ? ' error' : ''}`} placeholder="Delhi University" />
          {errors.collegeName && <div className="form-error">{errors.collegeName}</div>}
        </div>
      </div>

      <div className="form-section-title" style={{ marginTop: 8 }}>Program Selection</div>
      <div className="form-grid">
        <div className="form-group">
          <label>Year of Study <span>*</span></label>
          <select name="yearOfStudy" value={form.yearOfStudy} onChange={handleChange} className={`form-control${errors.yearOfStudy ? ' error' : ''}`}>
            <option value="">Select year</option>
            {['1st Year', '2nd Year', '3rd Year', '4th Year', 'Graduate', 'Other'].map(y => <option key={y}>{y}</option>)}
          </select>
          {errors.yearOfStudy && <div className="form-error">{errors.yearOfStudy}</div>}
        </div>
        <div className="form-group">
          <label>Select Course <span>*</span></label>
          <select name="course" value={form.course} onChange={handleChange} className={`form-control${errors.course ? ' error' : ''}`}>
            <option value="">Choose a program</option>
            {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
          </select>
          {errors.course && <div className="form-error">{errors.course}</div>}
        </div>
      </div>

      <div className="form-group">
        <label>Message (Optional)</label>
        <textarea name="message" value={form.message} onChange={handleChange} className="form-control" rows={3} placeholder="Tell us why you're interested in this program, your current background, or any questions..." style={{ resize: 'vertical' }} />
      </div>

      <button type="submit" className="btn btn-accent btn-lg" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
        {loading ? <><div className="spinner" style={{ width: 20, height: 20, borderWidth: 3 }} />Submitting...</> : <><FiSend size={18} />Submit Application — It's Free</>}
      </button>

      <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--mid)', marginTop: 12 }}>
        🔒 Your information is secure. No spam, ever.
      </p>
    </form>
  );
};

export default ApplicationForm;
